/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { APPLICATION_VIEW_OFFICE } from '@gh/builder-shared-data';
import { createRoute } from '@gh/builder-shared';
import { CoreLazyModule, LAZY_SYMBOLS, lazyActivatorRoute } from '@gh/core-lazy';

import {
  LeadBoardBusinessService,
  LeadBoardComponent,
  LeadDetailsBusinessService,
  LeadDetailsTabContainerComponent,
  LeadListBusinessService,
  LeadListComponent,
  QuoteDetailsBusinessService,
  QuoteDetailsComponent,
} from './leads';
import { salesManagementReportFilterDialogRoute } from './leads/components/lead-list/sales-management-report-filter-dialog/sales-management-report-filter-dialog.route';
import { RfqEDocListTabComponent } from './leads/components/rfq/rfq-e-doc-list-tab/rfq-e-doc-list-tab.component';
import { RfqGeneralTabComponent } from './leads/components/rfq/rfq-general-tab/rfq-general-tab.component';
import { RfqQuotationListTabComponent } from './leads/components/rfq/rfq-quotation-list-tab/rfq-quotation-list-tab.component';
import { RfqSitesTabComponent } from './leads/components/rfq/rfq-site-list-tab/rfq-site-list-tab.component';
import { RfqTabContainerComponent } from './leads/components/rfq/rfq-tab-container.component';
import { RfqSiteDetailsComponent } from './leads/components/site-details/rfq-site-details.component';
import { RfqBusinessService } from './leads/resources/rfq-business.service';
import { SiteDetailsBusinessService } from './leads/resources/site-details-business.service';
import {
  SalespersonBudgetListBusinessService,
  SalespersonBudgetListComponent,
} from './salesperson-budgets';
import { RfqManagementListComponent } from './rfq-management/components/rfq-management-list/rfq-management-list.component';
import { RfqManagementListBusinessService } from './rfq-management/resources/rfq-management-list-business.service';
import { HlpListBusinessService } from './hlp/resources/hlp-list-business.service';
import { HlpListComponent } from './hlp/components/hlp-list/hlp-list.component';
import { HlpDetailsTabContainerComponent } from './hlp/components/hlp-details/hlp-details-tab-container.component';
import { HlpDetailsBusinessService } from './hlp/resources/hlp-details-business.service';

export const routes: Routes = [
  lazyActivatorRoute,
  createRoute({
    path: 'leads',
    component: LeadListComponent,
    init: LeadListBusinessService,
    auth: {
      permission: 'Sales.Leads.LIST',
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    path: 'rfq-management',
    component: RfqManagementListComponent,
    init: RfqManagementListBusinessService,
    auth: {
      permission: 'Sales.Leads.LIST',
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    path: 'hlp',
    component: HlpListComponent,
    init: HlpListBusinessService,
    auth: {
      permission: 'Sales.Hlp.LIST',
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    component: HlpDetailsTabContainerComponent,
    path: 'hlp/new',
    init: HlpDetailsBusinessService,
    leave: true,
    auth: {
      permission: 'Sales.Hlp.CREATE',
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    component: HlpDetailsTabContainerComponent,
    path: 'hlp/:hlpId',
    init: HlpDetailsBusinessService,
    leave: true,
    auth: {
      permission: [
        'Sales.Hlp.EDIT',
        'Sales.Hlp.VIEW',
      ],
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
                path: 'leads-board',
                component: LeadBoardComponent,
                init: LeadBoardBusinessService,
                auth: {
                  permission: [
                    'Sales.Leads.LIST',
                    'Sales.Leads.EDIT',
                  ],
                  applicationView: APPLICATION_VIEW_OFFICE,
                },
              }),
  createRoute({
    component: LeadDetailsTabContainerComponent,
    path: 'leads/new',
    init: LeadDetailsBusinessService,
    leave: true,
    auth: {
      permission: 'Sales.Leads.CREATE',
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    component: LeadDetailsTabContainerComponent,
    path: 'leads/:leadId',
    init: LeadDetailsBusinessService,
    leave: true,
    auth: {
      permission: [
        'Sales.Leads.EDIT',
        'Sales.Leads.VIEW',
      ],
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    path: 'sales-folders/:salesFolderId/rfq/quotations/new',
    component: QuoteDetailsComponent,
    init: QuoteDetailsBusinessService,
    leave: true,
    auth: {
      permission: [
        'Sales.Leads.EDIT',
      ],
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    path: 'sales-folders/:salesFolderId/rfq/quotations/:quoteId',
    component: QuoteDetailsComponent,
    init: QuoteDetailsBusinessService,
    leave: true,
    auth: {
      permission: [
        'Sales.Leads.EDIT',
        'Sales.Leads.VIEW',
      ],
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    component: RfqTabContainerComponent,
    path: 'sales-folders/:salesFolderId/rfq',
    init: RfqBusinessService,
    children: [
      createRoute({
        path: 'general',
        component: RfqGeneralTabComponent,
        leave: true,
        init: {
          service: RfqBusinessService,
          selector: 'initRfqGeneralTab',
        },
      }),
      createRoute({
        path: 'sites',
        component: RfqSitesTabComponent,
        init: {
          service: RfqBusinessService,
          selector: 'initRfqSitesTab',
        },
      }),
      createRoute({
        path: 'quotations',
        component: RfqQuotationListTabComponent,
        init: {
          service: RfqBusinessService,
          selector: 'initRfqQuotationsTab',
        },
      }),
      createRoute({
        path: 'edocs',
        component: RfqEDocListTabComponent,
        init: {
          service: RfqBusinessService,
          selector: 'initRfqEDocListTab',
        },
      }),
    ],
    auth: {
      applicationView: [APPLICATION_VIEW_OFFICE],
      permission: [
        'Sales.Leads.EDIT',
        'Sales.Leads.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'sales-folders/:salesFolderId/site/new',
    component: RfqSiteDetailsComponent,
    init: SiteDetailsBusinessService,
    leave: true,
    auth: {
      permission: [
        'Sales.Leads.CREATE',
      ],
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    path: 'sales-folders/:salesFolderId/site/:siteId',
    component: RfqSiteDetailsComponent,
    leave: true,
    init: SiteDetailsBusinessService,
    auth: {
      permission: [
        'Sales.Leads.EDIT',
        'Sales.Leads.VIEW',
      ],
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
  createRoute({
    path: 'salesperson-budgets',
    component: SalespersonBudgetListComponent,
    init: SalespersonBudgetListBusinessService,
    leave: true,
    auth: {
      permission: ['Sales.Budgets.VIEW', 'Sales.Budgets.EDIT'],
      applicationView: APPLICATION_VIEW_OFFICE,
    },
  }),
];

export function salesLazySymbolsFactory(): Hash<any> {
  return { salesManagementReportFilterDialogRoute };
}

@NgModule({
  imports: [
    CoreLazyModule.forChild(),
    RouterModule.forChild(routes),
  ],
  declarations: [],
  providers: [
    { provide: LAZY_SYMBOLS, useFactory: salesLazySymbolsFactory, deps: [] },
  ],
  exports: [RouterModule],
})
export class BuilderSalesRoutingModule {
}
